"use client";

/**
 * Full lessons experience for /lessons: current player + history list.
 */

import LessonPlayer from "./LessonPlayer";
import { useLessonSession } from "./useLessonSession";
import {
  formatDurationMs,
  formatLessonTitle,
  selfRatingLabel,
} from "./lessonCardState";

interface LessonsExperienceProps {
  language: string;
}

function formatCreatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function LessonsExperience({ language }: LessonsExperienceProps) {
  const session = useLessonSession(language);
  const { history, lesson, loading } = session;

  return (
    <div className="space-y-8">
      <LessonPlayer session={session} heading="Current lesson" />

      <section aria-label="Lesson history">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-app-muted">
          History
        </h2>

        {loading && history.length === 0 ? (
          <p className="mt-3 text-sm text-app-body">Loading past lessons…</p>
        ) : history.length === 0 ? (
          <p className="mt-3 text-sm text-app-body">
            Past lessons will show up here after you generate your first one.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-app-border overflow-hidden rounded-md border border-app-border bg-app-surface">
            {history.map((row) => {
              const isCurrent = row.id === lesson?.id;
              return (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-app-text">
                      {formatLessonTitle(row)}
                      {isCurrent ? (
                        <span className="ml-2 text-xs font-normal text-app-teal">
                          current
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-0.5 text-xs text-app-muted">
                      {formatCreatedAt(row.created_at)}
                      {" · "}
                      {formatDurationMs(row.duration_ms)}
                      {row.new_count != null || row.review_count != null ? (
                        <>
                          {" · "}
                          {row.new_count ?? 0} new
                          {" · "}
                          {row.review_count ?? 0} review
                        </>
                      ) : null}
                    </p>
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    {row.self_rating ? (
                      <span className="inline-flex rounded-full border border-app-border bg-app-tinted px-2 py-0.5 font-medium text-app-body">
                        {selfRatingLabel(row.self_rating) ?? row.self_rating}
                      </span>
                    ) : (
                      <span className="text-app-muted-dim">Unrated</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
